import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EventEventTypeEnum,
  FetchEventsResponse,
  PaginationResponse,
  OrderByDirection,
} from '../../swagger/models'
import EventSummaryTable from './EventSummaryTable'
import Header, { HeaderVariant } from '../Elements/Header'
import EventCardMobile from '../Card/EventCardMobile'
import SearchBar from '../Search/SearchBar'
import DropDown, { DropDownVariant } from '../Menus/DropDown'
import OutlinedButton, {
  OutlinedButtonVariant,
} from '../Buttons/OutlinedButton'
import TodaysEventCard from '../Card/TodaysEventCard'
import { Page } from '../Elements/PageMargins'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { createSpacetime, EventFilter, getTodaysEarliestEvent } from './Events'
import spacetime from 'spacetime'
import { TFunction } from 'i18next'
import Tooltip from '@mui/material/Tooltip'
import DateField, { DateFieldVariant } from '../Elements/DateField'
import { useNavigate } from 'react-router'
import { styled } from '@mui/system'
import Box from '@mui/material/Box'
import { EventSortBy, EventSortOptions } from './EventEnums'
import { Help } from '@mui/icons-material'
import CardFormHeader from '../Card/CardFormHeader'
import Card from '@mui/material/Card'
import useEventReassignment from '../../hooks/useEventReassignment'
import { useAuth } from '../Routes/AuthProvider'
import { Can } from '@casl/react'
import ReassignEventOwnerModal from '../Modals/ReassignEventOwnerModal'
import { validEventTypes } from './validEventTypes'

const labelForEventTypesFilter = (sortOption: string, t: TFunction) => {
  switch (sortOption) {
    case EventTypes.InformationMeeting:
      return t('Events.EventTypes.InformationMeeting', 'Information Meeting')
    case EventTypes.OpenHouse:
      return t('Events.EventTypes.OpenHouse', 'Open House')
    case EventTypes.Practicum:
      return t('Events.EventTypes.Practicum', 'Practicum')
    default:
      return t('Events.EventTypes.AllEventTypes', 'All Event Types')
  }
}

const StyledHeader = styled(Header)(({ theme }) => ({
  margin: theme.spacing(3, 'auto'),
  padding: 0,
}))

interface EventsTabProps {
  events: FetchEventsResponse[]
  isLoading: boolean
  minDateFilter: string
  dateToFilter: string
  handleEventFilterChange: <K extends keyof EventFilter>(
    filter: K,
    value: EventFilter[K]
  ) => void
  handlePaginationChange: (pagination: PaginationResponse) => void
  pagination: PaginationResponse
}

const EventTypes = {
  AllEvents: 'All Event Types',
  ...EventEventTypeEnum,
}

const labelForSortOption = (
  sortOption: EventSortOptions,
  t: TFunction
): string => {
  switch (sortOption) {
    case EventSortOptions.EventName:
      return t('Team.SortOptions.EventName', 'Sort By: Event Name')
    case EventSortOptions.StartTime:
      return t('Team.SortOptions.StartTime', 'Sort By: Start Time')
    case EventSortOptions.CreatedBy:
      return t('Team.SortOptions.CreatedBy', 'Sort By: Created By')
    default:
      return t('Team.SortOptions.StartDate', 'Sort By: Start Date')
  }
}

export const EventsTab: React.FunctionComponent<EventsTabProps> = (props) => {
  const {
    events,
    isLoading,
    minDateFilter,
    dateToFilter,
    handleEventFilterChange,
    handlePaginationChange,
    pagination,
  } = props

  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const { permissionAbility } = useAuth()
  const eventsReassignment = useEventReassignment({
    onReassignComplete: handlePaginationChange,
    currentPage: pagination.page,
    pageSize: pagination.pageSize,
    totalCount: pagination.totalCount,
    events,
  })
  const isMobileOrSmaller = useMediaQuery(theme.breakpoints.down('sm'))
  const [filterType, setFilterType] = useState('')
  const [paramValue, setParamValue] = useState('')

  const todayEvent = useMemo(() => getTodaysEarliestEvent(events), [events])

  let isNextEventToday = true

  if (todayEvent) {
    isNextEventToday = spacetime
      .now()
      .isSame(createSpacetime(todayEvent), 'day')
  }

  const translationText = t(
    'Events.AddTittleToolTip.TittleTranslation',
    'Search for events by date, past or future, to see all events on that date and after'
  )

  const dateOnOrAfterFilter = () => (
    <Box display="flex" alignItems="center">
      <DateField
        dateVariant={DateFieldVariant.EventsByDate}
        inputProps={{
          min: `${minDateFilter}`,
          ref: React.createRef(),
        }}
        variant="filled"
        type="date"
        fullWidth={true}
        onChangeForParent={handleDateChange}
        value={dateToFilter}
        onKeyDown={handleOnkeyDown}
        hideLabel
        sx={{
          '& input': {
            height: '100%', //make sure that the text is aligned correctly
            padding: theme.spacing(1.3), // Adjusts the internal padding
          },
        }}
        InputProps={{
          disableUnderline: true,
          sx: {
            height: theme.spacing(4.5),
          },
        }}
      />
      <Tooltip title={translationText}>
        <Help />
      </Tooltip>
    </Box>
  )

  const handleDateChange = (Event: React.ChangeEvent<HTMLInputElement>) => {
    handleEventFilterChange('fromDate', Event.target.value)
  }
  const handleOnkeyDown = (Event: React.KeyboardEvent<HTMLInputElement>) => {
    Event.preventDefault()
  }

  const toEventDetailsPage = (id: number, eventName: string) => {
    navigate(
      {
        pathname: `/events/event-details/${id}`,
      },
      {
        state: { eventName },
      }
    )
  }

  const handleDropDownSelection = (selection: string, id: string) => {
    switch (id) {
      case 'filterByEventType':
        setParamValue(selection)
        setFilterType('eventType')
        break
      case 'filterByEventCreator':
        setParamValue(selection)
        setFilterType('eventCreator')

        break
      // dropdown sort is only available in mobile version
      case 'sort':
        let orderByProperty: string = EventSortBy.StartDate
        switch (selection) {
          case EventSortOptions.StartDate:
            orderByProperty = EventSortBy.StartDate
            break
          case EventSortOptions.StartTime:
            orderByProperty = EventSortBy.StartTime
            break
          case EventSortOptions.CreatedBy:
            orderByProperty = EventSortBy.CreatedBy
            break
          case EventSortOptions.EventName:
            orderByProperty = EventSortBy.EventName
            break
          default:
            break
        }

        let orderDirection = OrderByDirection.Asc
        if (Object.keys(pagination.orderBy[0]).includes(orderByProperty)) {
          // we use the 0 index because we are using a single dropdown to select the
          // ordering so we only can apply the ordering one by the time
          orderDirection = Object.values(pagination.orderBy[0]).includes(
            OrderByDirection.Asc
          )
            ? OrderByDirection.Desc
            : OrderByDirection.Asc
        }

        handlePaginationChange({
          ...pagination,
          page: 0,
          orderBy: [{ [orderByProperty]: orderDirection }],
        })

        break
    }
  }

  const handleSearch = (searchText: string) => {
    setFilterType('search')
    setParamValue(searchText)
  }

  useEffect(() => {
    handleEventFilterChange(filterType as keyof EventFilter, paramValue)
  }, [handleEventFilterChange, filterType, paramValue])

  return (
    <Page>
      {isMobileOrSmaller ? (
        <>
          <StyledHeader
            id="todayEventHeader"
            headerName={
              isNextEventToday
                ? t('Events.Card.Header.Today', "Today's Event")
                : t('Events.Card.Header.Upcoming', 'Upcoming Event')
            }
          />

          {todayEvent !== undefined ? (
            <EventCardMobile
              event={todayEvent}
              todaysEvent={true}
              isLoading={isLoading}
            />
          ) : null}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              margin: theme.spacing(0, 'auto'),
            }}
          >
            <SearchBar handleSearch={handleSearch} />
          </Box>
          <section>
            <Box display="flex" justifyContent={'space-between'}>
              <DropDown
                id="filterByEventType"
                menuOptions={Object.values(EventTypes)
                  .filter((eventType) => validEventTypes.includes(eventType))
                  .map((option) => ({
                    name: labelForEventTypesFilter(option, t),
                    id: option,
                  }))}
                defaultValue={t('Events.FilterOption.EventTypes', 'All Events')}
                handleSelection={handleDropDownSelection}
                formControlProps={{
                  width: `calc(100% - ${theme.spacing(2)})`,
                  marginRight: theme.spacing(2),
                  [theme.breakpoints.down('sm')]: {
                    width: '100%',
                    margin: theme.spacing(2),
                  },
                }}
                variant={DropDownVariant.SortAndFilter}
              />
            </Box>
            <Box textAlign="center">{dateOnOrAfterFilter()}</Box>
          </section>
          <StyledHeader
            id="eventsHeader"
            headerName={t('Events.Card.Header.Events', 'Events')}
          />

          <Box textAlign={'center'}>
            <DropDown
              fullWidth
              cssProps={{
                display: 'flex',
                justifyContent: 'center',
                maxWidth: `calc(100vw - ${theme.spacing(4)})`,
              }}
              id="sort"
              menuOptions={Object.values(EventSortOptions).map((option) => {
                return {
                  name: labelForSortOption(option, t),
                  id: option,
                }
              })}
              defaultValue={t('Events.SortOption.Sort', 'Sort')}
              handleSelection={handleDropDownSelection}
              variant={DropDownVariant.SortAndFilter}
            />
          </Box>

          {events.map((event) =>
            event !== undefined ? (
              <EventCardMobile
                key={event.eventKey}
                event={event}
                isLoading={isLoading}
              />
            ) : null
          )}
          {/** Hide button once we are viewing all available Events */}
          {pagination.totalCount > pagination.pageSize && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: theme.spacing(6, 0),
              }}
            >
              <OutlinedButton
                data-testid="viewMoreButton"
                id="viewMoreDraftEvents"
                onClick={() =>
                  handlePaginationChange({
                    ...pagination,
                    pageSize: pagination.pageSize + 10, // add 10 more events to the page
                    page: 0, /// Reset the page to the first page
                  })
                }
                variant={OutlinedButtonVariant.ViewMore}
              />
            </Box>
          )}
        </>
      ) : (
        <>
          {todayEvent && (
            <TodaysEventCard
              isLoading={isLoading}
              onClick={() =>
                toEventDetailsPage(
                  (todayEvent.eventKey as number) ?? '',
                  todayEvent.name
                )
              }
              todaysEvent={todayEvent}
              headerLabel={
                isNextEventToday
                  ? t('Events.Card.Header.Today', "Today's Event")
                  : t('Events.Card.Header.Upcoming', 'Upcoming Event')
              }
            />
          )}
          {eventsReassignment && (
            <ReassignEventOwnerModal
              open={eventsReassignment.isReassignModalOpen}
              onClose={eventsReassignment.handleCloseReassignModal}
              onReassign={eventsReassignment.handleReassignOwners}
              selectedCount={eventsReassignment.selectedRows.length}
              selectedEventOwner={eventsReassignment.selectedOwner}
              setSelectedEventOwner={eventsReassignment.setSelectedOwner}
              eventOwnerOptions={eventsReassignment.ownerOptions}
              isLoading={eventsReassignment.isLoadingOwners}
              isReassigning={eventsReassignment.isReassigning}
              onInputChange={eventsReassignment.setOwnerSearchInput}
            />
          )}
          <Card>
            <CardFormHeader
              header={
                <Header
                  id="transcripts-header"
                  headerName={t('Events.Card.Header', 'Events')}
                  component="h2"
                  variant={HeaderVariant.Card}
                />
              }
              buttons={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Can I="bulkReassign" on="Event" ability={permissionAbility}>
                    <Box
                      sx={{
                        margin: theme.spacing(0, 3),
                      }}
                    >
                      <OutlinedButton
                        id="reassignEventOwners"
                        variant={OutlinedButtonVariant.ReassignEvents}
                        onClick={eventsReassignment.handleOpenReassignModal}
                        disabled={eventsReassignment.selectedRows.length === 0}
                      />
                    </Box>
                  </Can>
                  <Box>
                    <SearchBar
                      handleSearch={handleSearch}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          margin: 0,
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      margin: theme.spacing(0, 3),
                    }}
                  >
                    <DropDown
                      id="filterByEventType"
                      menuOptions={Object.values(EventTypes)
                        .filter((eventType) =>
                          [...validEventTypes, 'All Event Types'].includes(
                            eventType
                          )
                        )
                        .map((option) => {
                          return {
                            name: labelForEventTypesFilter(option, t),
                            id: option,
                          }
                        })}
                      defaultValue={t(
                        'Events.FilterOption.EventTypes',
                        'All Event Types'
                      )}
                      handleSelection={handleDropDownSelection}
                      variant={DropDownVariant.SortAndFilter}
                    />
                  </Box>

                  <Tooltip
                    data-testid="tooltip-events-by-date"
                    title={t('Tooltip.Title.EventsByDate', 'Events By Date')}
                  >
                    {dateOnOrAfterFilter()}
                  </Tooltip>
                </Box>
              }
            />
            <Box>
              <EventSummaryTable
                events={events}
                isLoading={isLoading}
                handlePaginationChange={handlePaginationChange}
                pagination={pagination}
                checkboxSelection={permissionAbility.can(
                  'bulkReassign',
                  'Event'
                )}
                onSelectionModelChange={
                  eventsReassignment.handleSelectionChange
                }
                selectionModel={eventsReassignment.selectedRows}
              />
            </Box>
          </Card>
        </>
      )}
    </Page>
  )
}

export default EventsTab
